import { FunctionComponent, h } from 'preact';
import { useRef, useState, useEffect } from 'preact/hooks';
import { LineItem } from '@pperform/pp-data-mappers';
import { StyledComponent } from 'styled-components';
import { CardFlip } from './FlippingCard.styles';
import { CardFrontProps } from '../CardFront';
import { CardBackProps } from '../CardBack';

interface FlippingCardProps {
  cardFront: FunctionComponent<CardFrontProps>;
  cardBack: FunctionComponent<CardBackProps>;
  flippingCardContainer: StyledComponent<'div', any>;
  card: LineItem;
}

const FlippingCard = ({
  cardFront: CardFront,
  cardBack: CardBack,
  flippingCardContainer: FlippingCardContainer,
  card,
}: FlippingCardProps): JSX.Element => {
  const [isFlipped, setIsFlipped] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const ariaLabel = card.productName ?? '';

  const handleMouseEnter = () => {
    setIsFlipped(true);
  };

  const handleMouseLeave = () => {
    setIsFlipped(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only handle flipping if the event target is the container itself
    if (e.currentTarget === e.target && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setIsFlipped(!isFlipped);
    }
  };

  // Update parent container aria-pressed when flipped state changes
  useEffect(() => {
    if (containerRef.current) {
      const parentContainer = containerRef.current.closest(
        '[data-card-container]',
      ) as HTMLElement;
      if (parentContainer) {
        parentContainer.setAttribute('aria-pressed', isFlipped.toString());
      }
    }
  }, [isFlipped]);

  return (
    <FlippingCardContainer
      ref={containerRef}
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      aria-pressed={isFlipped}
      style={{
        position: 'relative',
      }}
    >
      <CardFlip ref={cardRef} isFlipped={isFlipped}>
        <CardFront card={card} ariaHidden={isFlipped} />
        <CardBack card={card} ariaHidden={!isFlipped} />
      </CardFlip>
    </FlippingCardContainer>
  );
};

export { FlippingCard };
